// packages import
import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { FC, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
// component import
import { Alert } from "../../common/Alert";
import { CustomController } from "../../common/CustomController";
import { CustomModal } from "../../common/CustomModal";
// others
import { UpdateNoteInput, useUpdateNoteMutation } from "../../../../generated";
import { NOT_APPLICABLE } from "../../../constants";
import { CaseContext } from "../../../context";
import { DeleteNoteModalProps, ErrorException } from "../../../interfaceTypes";
import { addCaseNoteValidationSchema } from "../../../validationSchema";
import { CaseActionType } from "../../cases/caseReducer";

export const EditNote: FC<DeleteNoteModalProps> = ({ refetchAllNotes, refetchCase }) => {
  const { isNoteEditOpen, currentNote, dispatch } = useContext(CaseContext)
  const { id, description } = currentNote || {}
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const handleClose = () => {
    setIsEditOpen(false);
    resetFields();
    dispatch({ type: CaseActionType.SET_IS_NOTE_EDIT_OPEN, isNoteEditOpen: false });
  }
  const [updateNote, { loading }] = useUpdateNoteMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
      else {
        Alert.error("Something went wrong")
      }
    },

    onCompleted(data) {
      if (data) {
        const { updateNote } = data
        const { response } = updateNote || {}
        const { status } = response || {}

        if (status === 200) {
          refetchAllNotes && refetchAllNotes()
          Alert.success('Note edited successfully')
          refetchCase && refetchCase();
          setIsEditOpen(false);
          dispatch({ type: CaseActionType.SET_IS_NOTE_EDIT_OPEN, isNoteEditOpen: false })
        }
      }
    }
  })

  const methods = useForm<UpdateNoteInput>({
    mode: 'all',
    resolver: yupResolver(addCaseNoteValidationSchema),

    defaultValues: {
      description,
    }
  })

  const { handleSubmit, reset } = methods;

  const onSubmit = async (data: UpdateNoteInput) => {
    if (id) {
      await updateNote({
        variables: {
          updateNoteInput: {
            id,
            description: data?.description as string
          }
        }
      });
    }
  }

  const resetFields = () => {
    reset({
      description: description || "",
    });
  }

  return (
    <CustomModal title="Edit note" isOpen={isNoteEditOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="end" style={{ alignItems: "baseline" }}>

            <Box style={{ alignItems: "baseline" }}>
              <IconButton
                sx={{ p: 0 }}
                color="primary"
                size="large"
                aria-label={isEditOpen ? 'back' : 'edit'}
                onClick={() => { setIsEditOpen(!isEditOpen); resetFields(); }}
              >
                {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
              </IconButton>

              {isEditOpen &&
                <IconButton color="primary" type='submit' disabled={loading} aria-label='save'>
                  <SaveIcon />
                </IconButton>
              }
            </Box>
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Typography variant='body2' fontSize='14px'>Note:</Typography>

              <Typography variant="h6" fontSize='16px' sx={{ fontWeight: 'normal' }}>
                {description || NOT_APPLICABLE}
              </Typography>
            </Collapse>

            <Collapse in={isEditOpen}>
              <CustomController
                controllerName="description"
                controllerLabel='Note'
                isMultiLine
                rowsLength={5}
              />
            </Collapse>
          </Box>
        </form>
      </FormProvider>

    </CustomModal>
  )
}